import { create } from 'zustand';
import type {
  CreateShippingMethodRequest,
  CreateShippingZoneRequest,
  ShippingZoneAdminDto,
  UpdateShippingMethodRequest,
  UpdateShippingZoneRequest,
  ZoneMemberKind,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Shipping zones, methods, and price bands (API → Store → Render, ADR-008).
 *
 * Every write returns the affected zone in full, so the store swaps one zone
 * rather than refetching the list: a price table only means anything beside the
 * zone it belongs to.
 */

interface ShippingState {
  zones: ShippingZoneAdminDto[];
  loading: boolean;
  saving: boolean;
  error: string | null;

  load: () => Promise<void>;
  createZone: (body: CreateShippingZoneRequest) => Promise<boolean>;
  updateZone: (id: string, body: UpdateShippingZoneRequest) => Promise<boolean>;
  addMember: (zoneId: string, kind: ZoneMemberKind, memberId: string) => Promise<boolean>;
  removeMember: (memberId: string) => Promise<boolean>;
  createMethod: (zoneId: string, body: CreateShippingMethodRequest) => Promise<boolean>;
  updateMethod: (methodId: string, body: UpdateShippingMethodRequest) => Promise<boolean>;
  deactivateMethod: (methodId: string) => Promise<boolean>;
}

export const useShippingStore = create<ShippingState>((set, get) => ({
  zones: [],
  loading: false,
  saving: false,
  error: null,

  async load() {
    set({ loading: true, error: null });

    try {
      const page = await withSession(() => adminApi.shipping.zones());
      set({ zones: page.items, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load shipping zones.') });
    }
  },

  createZone: (body) => run(set, get, () => adminApi.shipping.createZone(body), true),
  updateZone: (id, body) => run(set, get, () => adminApi.shipping.updateZone(id, body)),
  addMember: (zoneId, kind, memberId) =>
    run(set, get, () => adminApi.shipping.addMember(zoneId, { kind, memberId })),
  removeMember: (memberId) => run(set, get, () => adminApi.shipping.removeMember(memberId)),
  createMethod: (zoneId, body) => run(set, get, () => adminApi.shipping.createMethod(zoneId, body)),
  updateMethod: (methodId, body) =>
    run(set, get, () => adminApi.shipping.updateMethod(methodId, body)),
  deactivateMethod: (methodId) =>
    run(set, get, () => adminApi.shipping.deactivateMethod(methodId)),
}));

/**
 * One write, one zone back, one entry replaced.
 *
 * The API's refusals carry the reason — a gap in a price table, a city already
 * in another zone — and that message is far more useful than anything this
 * layer could invent, so it is shown as-is.
 */
async function run(
  set: (partial: Partial<ShippingState>) => void,
  get: () => ShippingState,
  call: () => Promise<ShippingZoneAdminDto>,
  isNew = false,
): Promise<boolean> {
  set({ saving: true, error: null });

  try {
    const zone = await withSession(call);
    const zones = get().zones;

    set({
      saving: false,
      zones: isNew
        ? [...zones, zone]
        : zones.map((entry) => (entry.id === zone.id ? zone : entry)),
    });
    return true;
  } catch (error) {
    set({ saving: false, error: errorMessage(error, 'Could not save this change.') });
    return false;
  }
}
